import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import ModalAdicionarLiberacaoDiversa from "./components/ModalAdicionarLiberacaoDiversa";
import { useAuth } from "shared/hooks/auth";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";
import { getEmpresas } from "services/modules/empresas";
import { IEmpresa } from "shared/interfaces/empresa";
import { api, apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

interface ILiberacaoDiversa {
  id: number;
  idEmpresa?: number | null;
  empresa: string;
  tipo?: number | null;
  tipoDescricao: string;
  liberacao: string;
  sistema: string;
  sistemaDestino?: string; // Segunda coluna de Sistema
  dataLiberacao?: string | null;
  ativo: boolean;
  /** Já disponibilizada (LiberacaoDiversa.Disponivel) — plano de 14/10/2026, Frente 5. */
  disponivel: boolean;
  /** Mesma regra do servidor: Liberar CEM (1), sistema 1..5 e pendente (revisão de 14/10, M8). */
  podeDisponibilizar: boolean;
}

/** Pares Projetista + Linha do tipo 4 (a liberação do MOF novo vale pelo par inteiro). */
interface IParTipologiaPrevia {
  idProjetista: number;
  idLinha: number;
  descricao: string;
  tipologiasMarcadas: number;
  temPacoteMofAntigo: boolean;
}

/** GET api/LiberacaoDiversaPacotes/Disponibilizar/{id}/Previa (MOF novo). */
interface IPreviaDisponibilizar {
  totalSeriais: number;
  seriaisBaseAntiga: number;
  seriaisMofNovo: number;
  seriaisClientesLiberacao: number;
  itensPorTabela: Record<string, number>;
  avisos: string[];
  /** Motivos que impedem o Disponibilizar (também presentes em avisos). */
  bloqueios: string[];
  pares: IParTipologiaPrevia[];
}

const STATUS_LIBERAR_CEM = 1;
const SISTEMA_MIN_MOF_NOVO = 1;
const SISTEMA_MAX_MOF_NOVO = 5;

type FilterKeyDiversas = "nomeLiberacao" | "sistema" | "data";

export const LiberacoesDiversas = () => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();

  // Estados principais
  const [liberacoes, setLiberacoes] = useState<ILiberacaoDiversa[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filtros
  const [empresas, setEmpresas] = useState<IEmpresa[]>([]);
  const [empresaFiltro, setEmpresaFiltro] = useState<number | "">("");
  const [tipoFiltro, setTipoFiltro] = useState<number | "">("");
  const [headerFilters, setHeaderFilters] = useState<Record<FilterKeyDiversas, string>>({
    nomeLiberacao: "",
    sistema: "",
    data: "",
  });
  const debouncedFilterColumn = useCallback(
    debounce((column: FilterKeyDiversas, value: string) => {
      setHeaderFilters((prev) => ({ ...prev, [column]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    [setHeaderFilters]
  );
  const filteredLiberacoes = useMemo(() => {
    return liberacoes.filter((liberacao) => {
      const nome = `${liberacao.tipoDescricao ?? ""} ${liberacao.liberacao ?? ""}`
        .trim()
        .toLowerCase();
      const sistema = liberacao.sistema?.toLowerCase() ?? "";
      const data =
        liberacao.dataLiberacao &&
        new Date(liberacao.dataLiberacao).toLocaleDateString("pt-BR").toLowerCase();

      const filtroNome = headerFilters.nomeLiberacao.trim().toLowerCase();
      const filtroSistema = headerFilters.sistema.trim().toLowerCase();
      const filtroData = headerFilters.data.trim().toLowerCase();

      if (filtroNome && !nome.includes(filtroNome)) return false;
      if (filtroSistema && !sistema.includes(filtroSistema)) return false;
      if (filtroData && (!data || !data.includes(filtroData))) return false;

      return true;
    });
  }, [headerFilters, liberacoes]);

  // Modal Adicionar/Editar Liberações Diversas (completo)
  const [modalAdicionarDiversa, setModalAdicionarDiversa] = useState<{
    isOpen: boolean;
    liberacaoId: number | null;
    somenteLeitura?: boolean;
  }>({ isOpen: false, liberacaoId: null });

  // Confirmação do Disponibilizar com a prévia (quantos clientes em cada mundo)
  const [modalDisponibilizar, setModalDisponibilizar] = useState<{
    isOpen: boolean;
    id?: number;
    previa?: IPreviaDisponibilizar;
  }>({ isOpen: false });

  // Modal de exclusão
  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    id?: number;
  }>({ isOpen: false });

  useEffect(() => {
    carregarEmpresas();
  }, []);

  useEffect(() => {
    carregarLiberacoes();
  }, [page, pageSize, empresaFiltro, tipoFiltro]);


  const carregarEmpresas = async () => {
    try {
      const data = await getEmpresas({ page: 1, pageSize: 1000 });
      setEmpresas(data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar empresas", type: "error" });
    }
  };

  const carregarLiberacoes = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
      };
      if (empresaFiltro) params.idEmpresa = empresaFiltro;
      if (tipoFiltro) params.tipo = tipoFiltro;

      // Lista do MOF novo: mesmos campos do ListaLiberacao do legado + Disponivel (Frente 5)
      const { data } = await api.get("LiberacaoDiversaPacotes/Lista", { params });

      const lista: ILiberacaoDiversa[] = (Array.isArray(data) ? data : data?.items || []).map(
        (item: any) => ({
          id: item.id ?? 0,
          idEmpresa: item.idEmpresa ?? null,
          empresa: item.empresa ?? "-",
          tipo: null,
          // O endpoint devolve o nome do tipo (como o legado) em "tipo"
          tipoDescricao: item.tipo ?? "-",
          liberacao: item.liberacao ?? "-",
          sistema: item.sistema ?? "-",
          sistemaDestino: item.sistema ?? "-",
          dataLiberacao: item.dataLiberacao ?? null,
          ativo: item.ativo ?? false,
          disponivel: !!item.disponivel,
          podeDisponibilizar:
            !item.disponivel &&
            item.statusId === STATUS_LIBERAR_CEM &&
            item.sistemaId >= SISTEMA_MIN_MOF_NOVO &&
            item.sistemaId <= SISTEMA_MAX_MOF_NOVO,
        }),
      );

      setLiberacoes(lista);
      setTotalRecords(lista.length);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
      setLiberacoes([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };


  const abrirDisponibilizar = async (id: number) => {
    setIsLoading(true);
    try {
      const { data } = await api.get<IPreviaDisponibilizar>(
        `LiberacaoDiversaPacotes/Disponibilizar/${id}/Previa`,
      );
      setModalDisponibilizar({ isOpen: true, id, previa: data });
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisponibilizar = async () => {
    if (!modalDisponibilizar.id) return;
    setIsLoading(true);
    try {
      const { data } = await api.post<IPreviaDisponibilizar>(
        `LiberacaoDiversaPacotes/Disponibilizar/${modalDisponibilizar.id}`,
      );
      openNotification({
        message: `Liberação disponibilizada para ${data?.totalSeriais ?? 0} cliente(s).`,
        type: "success",
      });
      setModalDisponibilizar({ isOpen: false });
      carregarLiberacoes();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverLiberacao = async () => {
    if (!modalDelete.id) return;
    setIsLoading(true);
    try {
      await apiAntigo.delete(`/LiberacaoDiversa/${modalDelete.id}`);
      openNotification({
        message: "Liberação diversa removida com sucesso!",
        type: "success",
      });
      setModalDelete({ isOpen: false });
      carregarLiberacoes();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "nomeLiberacao",
      headerName: "Nome da Liberação",
      flex: 2,
      minWidth: 250,
      valueGetter: (params) =>
        `${params.row.tipoDescricao ?? ""} ${params.row.liberacao ?? ""}`.trim(),
      renderHeader: () => (
        <FilterColumn
          label="Nome da Liberação"
          name="nomeLiberacao"
          onChange={(event) =>
            debouncedFilterColumn("nomeLiberacao", event.target.value)
          }
        />
      ),
    },
    {
      field: "sistema",
      headerName: "Sistema",
      flex: 1,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn
          label="Sistema"
          name="sistema"
          onChange={(event) =>
            debouncedFilterColumn("sistema", event.target.value)
          }
        />
      ),
    },
    {
      field: "sistemaDestino",
      headerName: "Sistema",
      flex: 1,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn
          label="Sistema"
          name="sistemaDestino"
          onChange={(event) =>
            debouncedFilterColumn("sistema", event.target.value)
          }
        />
      ),
      valueGetter: (params) => params.row.sistemaDestino || params.row.sistema || "-",
    },
    {
      field: "dataLiberacao",
      headerName: "Data",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
      renderHeader: () => (
        <FilterColumn
          label="Data"
          name="data"
          onChange={(event) => debouncedFilterColumn("data", event.target.value)}
        />
      ),
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => (params.row.disponivel ? "Disponibilizada" : "Pendente"),
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.disponivel ? "Disponibilizada" : "Pendente"}
          color={params.row.disponivel ? "success" : "warning"}
          variant="outlined"
        />
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
      <IconButton
        size="small"
        color="primary"
        title={params.row.disponivel ? "Consultar Liberação" : "Editar Liberação"}
        onClick={() =>
          setModalAdicionarDiversa({
            isOpen: true,
            liberacaoId: params.row.id,
            somenteLeitura: params.row.disponivel,
          })
        }
      >
        <SearchIcon fontSize="small" />
      </IconButton>
          {params.row.podeDisponibilizar && (
            <IconButton
              size="small"
              color="success"
              title="Disponibilizar"
              onClick={() => abrirDisponibilizar(params.row.id)}
            >
              <PublishIcon fontSize="small" />
            </IconButton>
          )}
          {/* Disponibilizada já gravou nos dois mundos: remover o cabeçalho não desfaz isso. */}
          {!params.row.disponivel && (
            <IconButton
              size="small"
              color="error"
              title="Remover"
              onClick={() => setModalDelete({ isOpen: true, id: params.row.id })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="LIBERAÇÕES DIVERSAS"
        label="Liberações Diversas (Produtos, Aplicabilidades, Tabelas, N2)"
        hasBackButton={false}
      />

      <Paper elevation={3}>
        {/* Barra superior apenas com botão de nova liberação */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} md="auto">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Abrir modal completo para criação
                  setModalAdicionarDiversa({ isOpen: true, liberacaoId: null });
                }}
              >
                Nova Liberação Diversa
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Tabela */}
        <Box sx={{ p: 2 }}>
          <TitleTable title="Liberações Diversas Cadastradas" />
          <StripedDataGrid
            columns={columns}
            rows={filteredLiberacoes}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={filteredLiberacoes.length}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            checkboxSelection={false}
          />
        </Box>
      </Paper>

      {/* Modal de Criação/Edição removido - agora usa ModalAdicionarLiberacaoDiversa para tudo */}

      {/* Modal Adicionar/Editar Liberações Diversas (completo) */}
      <ModalAdicionarLiberacaoDiversa
        open={modalAdicionarDiversa.isOpen}
        onClose={() => setModalAdicionarDiversa({ isOpen: false, liberacaoId: null })}
        liberacaoId={modalAdicionarDiversa.liberacaoId}
        somenteLeitura={!!modalAdicionarDiversa.somenteLeitura}
        onSuccess={() => {
          carregarLiberacoes();
          setModalAdicionarDiversa({ isOpen: false, liberacaoId: null });
        }}
      />

      {/* Confirmação do Disponibilizar com a prévia (plano de 14/10/2026, Frente 5) */}
      <Dialog
        open={modalDisponibilizar.isOpen}
        onClose={() => setModalDisponibilizar({ isOpen: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>Disponibilizar liberação</DialogTitle>
        <DialogContent>
          {modalDisponibilizar.previa && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              {modalDisponibilizar.previa.totalSeriais > 0 ? (
                <Typography>
                  {`Serão liberados ${modalDisponibilizar.previa.totalSeriais} clientes (` +
                    `${modalDisponibilizar.previa.seriaisBaseAntiga} da base antiga, ` +
                    `${modalDisponibilizar.previa.seriaisMofNovo} do MOF novo` +
                    (modalDisponibilizar.previa.seriaisClientesLiberacao > 0
                      ? `, ${modalDisponibilizar.previa.seriaisClientesLiberacao} escolhidos na liberação`
                      : "") +
                    "). Deseja continuar?"}
                </Typography>
              ) : (
                <Typography color="error">
                  Nenhum cliente a liberar: ninguém possui o pacote de "Liberar para quem possui" no
                  sistema desta liberação e nenhum cliente foi marcado.
                </Typography>
              )}
              {Object.keys(modalDisponibilizar.previa.itensPorTabela || {}).length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Linhas novas por tabela
                  </Typography>
                  {Object.entries(modalDisponibilizar.previa.itensPorTabela).map(([tabela, qtd]) => (
                    <Typography key={tabela} variant="body2" color="text.secondary">
                      {tabela}: {qtd}
                    </Typography>
                  ))}
                </Box>
              )}
              {(modalDisponibilizar.previa.pares || []).length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Pares Projetista + Linha liberados no MOF novo
                  </Typography>
                  {modalDisponibilizar.previa.pares.map((par) => (
                    <Typography
                      key={`${par.idProjetista}_${par.idLinha}`}
                      variant="body2"
                      color={par.temPacoteMofAntigo ? "text.secondary" : "error"}
                    >
                      {`${par.descricao} (${par.tipologiasMarcadas} tipologia(s) marcada(s)` +
                        (par.temPacoteMofAntigo ? ")" : " — sem pacote no MOF antigo)")}
                    </Typography>
                  ))}
                </Box>
              )}
              {(modalDisponibilizar.previa.bloqueios || []).length > 0 && (
                <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                  Disponibilizar bloqueado — veja os avisos abaixo.
                </Typography>
              )}
              {(modalDisponibilizar.previa.avisos || []).length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Avisos
                  </Typography>
                  {modalDisponibilizar.previa.avisos.map((aviso) => (
                    <Typography
                      key={aviso}
                      variant="body2"
                      color={
                        (modalDisponibilizar.previa?.bloqueios || []).includes(aviso)
                          ? "error"
                          : "text.secondary"
                      }
                    >
                      {aviso}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDisponibilizar({ isOpen: false })}>CANCELAR</Button>
          <Button
            variant="contained"
            onClick={handleDisponibilizar}
            disabled={
              isLoading ||
              !modalDisponibilizar.previa?.totalSeriais ||
              (modalDisponibilizar.previa?.bloqueios || []).length > 0
            }
          >
            DISPONIBILIZAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDeleteModal
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={handleRemoverLiberacao}
        title="Remover Liberação Diversa"
        description="Tem certeza que deseja remover esta liberação diversa? Esta ação não pode ser desfeita."
      />

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};

